import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { localised, useI18n, type CopyKey } from './i18n';
import {
  currentObjective,
  getChat,
  getMessageSources,
  getProgress,
  getResearchProgress,
  isError,
  sendChatMessage,
  type ApiError,
  type ChatMessage,
  type CitedSource,
  type Objective,
  type ProgressView,
  type SubjectProgress,
  markChatSeen,} from './api';
import { percent } from './Progress';
import Bubble, { Prose, clock } from './chat/Bubble';
import Composer from './chat/Composer';
import Header from './chat/Header';
import Sheet from './chat/Sheet';
import { bubbleKind, type BubbleKind } from './chat/kinds';
import {
  arrivedSince,
  bottomScrollTop,
  distanceFromEnd,
  isPinned,
  restoredScrollTop,
  wantsOlder,
  type ScrollBox,
} from './chat/scroll';
import { suggestionsFor } from './chat/suggest';
import { useFirstRun } from './onboarding/FirstRun';
import Samples from './onboarding/Samples';
import { useOwnedViewport } from './chat/viewport';
import Diagnostics from './chat/Diagnostics';
import { debugOn } from './debug';
import { onServiceWorkerMessage } from './swMessages';
import { showBadge } from './badge';
import ObjectiveTabs from './ObjectiveTabs';
import { iconCount } from './tabs';
import { ROUTES } from './routes';
import './chat/chat.css';

/**
 * The teaching conversation, in the app. F-100, rebuilt as the screen it is. F-112.
 *
 * The whole conversation, and since F-103 the only place it happens: the questions, the
 * briefings, the reminders,
 * the corrections, the daily summary, and whatever the learner typed back. Not a copy of
 * it — the same rows. Everything the sweep and the grader send is written to the message
 * log, and this screen is a reader of that log, which is what makes the two transports one
 * conversation rather than two that resemble each other.
 *
 * This is where learners spend nearly all of their time, and the rebuild is about three
 * things that a panel on a page could not do:
 *
 * 1. **It owns the viewport.** The thread is the only thing that scrolls, the composer is
 *    the last row of a column exactly as tall as the visible screen, and nothing moves when
 *    the keyboard opens. `chat/viewport.ts` is where that is argued, including which units
 *    and which positioning were rejected and why.
 * 2. **It never moves somebody who is reading.** The rule and the bug it comes from are in
 *    `chat/scroll.ts`. Everything in this file that touches `scrollTop` asks that module
 *    first.
 * 3. **A message says where it came from.** The log has recorded `nodeId` on questions and
 *    briefings since F-100 and the API simply was not returning it; now it does, and the
 *    progress panel's subject list — one call, already needed for the header's percentage —
 *    turns that id into a name and a mastery. Tapping the chip says how the learner is
 *    doing in that subject without leaving the conversation.
 *
 * Three older decisions are unchanged and still argued where they happen: the whole newest
 * page is refetched rather than a delta merged (`refresh`), the learner's own message
 * appears before the server has answered (`pending`), and the poll stops when nobody is
 * looking at it.
 */

/** What a failed send is called, in copy the learner can act on. */
const SEND_ERRORS: Record<string, CopyKey> = {
  empty: 'chat.error.empty',
  too_long: 'chat.error.long',
  not_found: 'chat.error.session',
  sign_in_required: 'chat.error.session',
  // The answer was marked and only the reply was lost. Saying "try again" here would be
  // wrong twice over: the question is no longer open, so a second send would be graded
  // against nothing, and the grade they are owed an explanation of already stands.
  reply_failed: 'chat.error.graded',
  grading_failed: 'chat.error.send',
  // A learner who has not described a plan yet. Since F-151 the chat is per objective, so
  // there is no thread for their words to go in — and without a key of its own this fell to
  // `chat.error.send`, which tells them to try again when trying again cannot work.
  no_objective: 'chat.error.no_objective',
  unreachable: 'chat.error.offline',
};

/**
 * How often the thread is refetched while somebody is watching it.
 *
 * There is no websocket in this system and adding one for this would be a second always-on
 * cost for a conversation that moves a few times an hour. Twenty seconds is chosen against
 * what actually arrives: the sweep runs every five minutes, so a question cannot be more
 * than twenty seconds stale in a way anybody notices, and a graded reply is fetched
 * immediately after the send rather than waited for.
 *
 * **It is kept, and the bug it was blamed for was not its fault.** A poll that pulled the
 * reader to the bottom every twenty seconds looks exactly like a poll that is too frequent;
 * the fault was the scrolling rule, and slowing the poll down would have made the same
 * interruption rarer rather than absent. See `chat/scroll.ts`.
 */
const POLL_MS = 20_000;

/**
 * How often the header's percentage is re-read, which is not how often the thread is.
 *
 * Progress is a fifteen-subject aggregate over the learner's whole review state — far and
 * away the more expensive of the two reads — and it moves only when an answer is marked.
 * Re-reading it on the chat's own twenty-second poll would multiply the cost of the most
 * expensive query in the API by fifteen for a number that changes a few times a day. So it
 * is read on arrival, five-minutely after that, and — the case that actually matters —
 * immediately after the learner answers something, which is the one moment they will look
 * at it to see whether it moved.
 */
const PROGRESS_MS = 5 * 60_000;

/** A newest page, sized to a phone and a half. The server caps it at 200 regardless. */
const PAGE = 50;

/** One message the learner has sent that the server has not confirmed yet. */
interface Pending {
  messageId: string;
  text: string;
  sentAt: string;
  /** Set when the send came back refused, so the bubble can say so and offer a retry. */
  failed?: CopyKey;
}

/**
 * An id the send can be retried with.
 *
 * Generated once per composed message and kept across retries, because it is half of what
 * makes a retry idempotent server-side — the other half is `sentAt`. `randomUUID` needs a
 * secure context, which is every context this app is served in; the fallback exists so a
 * browser without it degrades to a message that cannot be safely retried rather than to a
 * screen that throws on the first keystroke.
 */
function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Whether two rows of the log are the same row, field for field. */
function same(a: ChatMessage, b: ChatMessage): boolean {
  return (
    a.id === b.id &&
    a.direction === b.direction &&
    a.at === b.at &&
    a.text === b.text &&
    a.purpose === b.purpose &&
    a.via === b.via &&
    a.nodeId === b.nodeId &&
    a.itemId === b.itemId &&
    a.inReplyTo === b.inReplyTo &&
    // Compared because this function claims to compare field for field, and a claim like
    // that is only true while it is kept. The description is the only part of `media` the
    // browser holds and the only part the bubble renders; a row's picture does not change
    // in practice — it is written once, with the message — so this costs nothing and stops
    // the day it does from being a bubble that never updates.
    a.media?.alt === b.media?.alt
  );
}

/**
 * Everything seen so far and everything just fetched, as one thread.
 *
 * A union keyed on `id`, which is the row's own primary key — so a message that arrives in
 * two pages appears once, and one that has scrolled out of the newest page is not lost with
 * it. Sorted on the same id, because it is `<when>#<message id>` and therefore already
 * chronological: the server orders the log by exactly this string, so the screen and the
 * table cannot disagree about what "before" means. A message filed under the literal
 * `unknown` sorts to the end, which is where the server puts it too.
 *
 * **It returns the array it was given when nothing changed, and the objects inside it when
 * those did not change either**, which is the difference between this poll costing nothing
 * and costing a re-render of the whole thread three times a minute. Nineteen polls in
 * twenty change nothing at all; without the identity check every one of them would hand
 * React a new array of new objects, `Bubble`'s memo would never hold, and — worse than the
 * work — the layout effect below would run and touch `scrollTop` on a thread nobody had
 * asked to move.
 */
function merge(seen: ChatMessage[] | undefined, arrived: ChatMessage[]): ChatMessage[] {
  const current = seen ?? [];
  const byId = new Map(current.map((message) => [message.id, message]));
  let changed = false;
  for (const message of arrived) {
    const held = byId.get(message.id);
    if (held && same(held, message)) continue;
    byId.set(message.id, message);
    changed = true;
  }
  if (!changed) return current;
  return [...byId.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * The day a message belongs to, as a heading.
 *
 * "Today" and "Yesterday" rather than the date for the two days a learner is actually
 * reading — which on this screen is nearly always — because "Tuesday, 15 September" is the
 * form you use for something you have to look up, and today is not something anybody looks
 * up. Older days keep the full form, where the weekday is genuinely the useful part.
 */
function dayOf(at: string, locale: string, t: (key: CopyKey) => string): string {
  const parsed = Date.parse(at);
  if (Number.isNaN(parsed)) return '';
  const when = new Date(parsed);
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const days = Math.floor((midnight.getTime() - when.getTime()) / 86_400_000);
  if (days < 0) return t('chat.day.today');
  if (days === 0) return t('chat.day.yesterday');
  return when.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
}

export default function Chat() {
  const { t, locale } = useI18n();

  /**
   * Which plan this screen is about, when something said. F-151 step 6.
   *
   * **`?o=` is how a tapped notification names its plan**, put there by `chatPathFor` on the
   * push payload's path and carried through the worker and the router untouched. The chat is
   * per plan and so is the open question, so a reply typed after following a notification has
   * to be attributed to the plan that notification was about — not to whichever plan was
   * asked most recently, which is what the API assumes when nobody says.
   *
   * Passed to the thread read, the send, and the header's two reads together, so all four
   * agree. Absent — an ordinary visit to `/chat`, or an old worker's notification — and every
   * one of them falls back to the same default, which is the property that matters: they
   * cannot disagree, whichever way it goes.
   *
   * From the router rather than `window.location`, because a tap while the app is already
   * open navigates in place: `App` hands the path to the router and nothing reloads, so a
   * value read once at mount would be the previous plan's for the rest of the session.
   */
  const { search } = useLocation();
  const objectiveId = useMemo(
    () => new URLSearchParams(search).get('o') ?? undefined,
    [search],
  );

  /*
   * `undefined` until the first page has been read, and the thread renders a line of copy
   * until then. An empty array would say "you have never been asked anything", which on a
   * learner three weeks into a curriculum is alarming in a way a moment of blankness is
   * not — the same distinction `App.tsx` makes between "not asked yet" and "signed out".
   */
  const [messages, setMessages] = useState<ChatMessage[] | undefined>();
  const [olderCursor, setOlderCursor] = useState<string | undefined>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  /*
   * A failed *history* fetch, which is not the same state as a failed first read.
   *
   * Its own flag because the first read's failure is "there is no conversation on screen
   * and here is why", and this one is "the conversation is fine and the page you asked for
   * did not come". Folding them into one would mean a failed poll twenty seconds later
   * clearing the message about the page that is still missing — or worse, the error copy
   * for an empty screen rendering underneath a full one.
   */
  const [olderFailed, setOlderFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<Pending[]>([]);
  const [unread, setUnread] = useState(0);
  const [objective, setObjective] = useState<Objective | null | undefined>();
  /**
   * Every live plan, for the tab bar and for the icon's total (F-151 step 8).
   *
   * Out of the `currentObjective()` call this screen already makes for the header, so the tabs
   * cost no request. `undefined` until it lands and on a failure, which the bar renders as no
   * bar rather than as "you have one plan".
   */
  const [plans, setPlans] = useState<Objective[] | undefined>();
  /*
   * The same list, where the poll can read it without depending on it.
   *
   * `refresh` is a `useCallback` keyed on the plan being shown, and the poll below is built on
   * its identity — so adding the plans to its dependencies would tear down and restart the
   * twenty-second loop every time the list was re-read. The badge needs the newest list and
   * nothing else needs the render, which is exactly what a ref is for.
   */
  const plansRef = useRef<Objective[] | undefined>(undefined);
  const [progress, setProgress] = useState<ProgressView | null | undefined>();
  const [asked, setAsked] = useState<ChatMessage | undefined>();
  /*
   * Where the tapped message's material came from, and one counter to ask again with.
   *
   * Three states in one value rather than a `sources` array beside a `loading` flag beside a
   * `failed` flag: the three are mutually exclusive and the bug that separate flags produce
   * is the one already recorded above for `loadFailed`/`olderFailed` — a stale array
   * rendering under a failure, or a failure rendering under a list that did arrive. `attempt`
   * is what the retry button moves, because re-running an effect needs a dependency to
   * change and the tapped message has not.
   */
  const [provenance, setProvenance] = useState<Provenance | undefined>();
  const [attempt, setAttempt] = useState(0);

  const root = useRef<HTMLElement | null>(null);
  const thread = useRef<HTMLDivElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);

  /*
   * Whether a page has ever arrived, which is what separates "the first read failed" from
   * "a poll failed". Held in a ref rather than derived from `messages` because `refresh`
   * is a stable callback and reading state through it would either stale-close over the
   * first render's value or make the poll's interval restart on every message.
   */
  const loaded = useRef(false);

  /**
   * Which plan the thread on screen actually belongs to, as the API answered it.
   *
   * A ref and not state, for `loaded`'s reason: `refresh` is a stable callback and reading
   * this through state would either close over the first render's value or restart the poll
   * on every message. It is the screen's answer to "is this arriving `?o=` a different
   * conversation or the one I am already showing", which the query string alone cannot
   * answer — a learner on `/chat` has no `?o=` and is nonetheless looking at a plan.
   */
  const showing = useRef<string | undefined>(undefined);

  /**
   * Which plan the newest in-flight request was for.
   *
   * **A request outlives the screen it was made for.** A `getChat` issued for plan A can
   * resolve after a notification has moved the screen to plan B, and `merge` is a union —
   * so its page would be folded into B's thread and the learner would read two plans'
   * messages interleaved by time with nothing saying why. That is exactly what the reset
   * above exists to prevent, arriving through the one door the reset cannot close. Every
   * response checks this before it writes anything.
   */
  const inFlightFor = useRef<string | undefined>(undefined);

  /*
   * Whether the learner is at the bottom of the thread right now.
   *
   * **A cache of a measurement, never an intention.** This is the single value the old
   * bug lived in: it used to mean "the screen would like to scroll to the bottom", it
   * started `true`, and only pressing "show earlier messages" cleared it — so reading back
   * with a finger left it set and every poll yanked the reader down. It is now written from
   * the scroll position on every scroll event and nowhere else, except the one place a
   * learner has unambiguously asked to be at the bottom: when they send something.
   */
  const pinned = useRef(true);

  /** The newest message the learner has actually had in front of them. */
  const lastSeen = useRef('');

  /**
   * The distance to the end of the thread, to restore after a page of history is prepended.
   *
   * Non-null only between `loadOlder` measuring and the layout effect applying it. See
   * `restoredScrollTop` for why the anchor is the end and not the top.
   */
  const keepFromEnd = useRef<number | null>(null);

  /**
   * The scroll position this component set itself, so the handler can ignore its own echo.
   *
   * Restoring a prepend produces a `scroll` event like any other, and treating that one as
   * the learner scrolling would immediately ask for another page — which asks for another,
   * until the whole history is in memory. Compared by value rather than counted, because a
   * browser may coalesce two programmatic scrolls into one event or none.
   */
  const settled = useRef<number | null>(null);

  useOwnedViewport(root);

  const boxOf = (element: HTMLElement): ScrollBox => ({
    scrollTop: element.scrollTop,
    scrollHeight: element.scrollHeight,
    clientHeight: element.clientHeight,
  });

  /**
   * The newest page, folded into what has already been seen.
   *
   * **The whole page, not a delta.** A `since` parameter would be cheaper and would need a
   * reconciliation rule — which rows are new, what to do about two written in the same
   * second, how to handle a retried send — and that rule is where chat screens lose and
   * duplicate messages. Fifty rows of a learner's own partition is a couple of read units
   * (docs/costs.md, "The chat in the app"), which is not a number worth a class of bug.
   *
   * **Folded rather than replacing, because replacing throws away history.** A learner who
   * paged back through a fortnight and then waited twenty seconds would watch the
   * conversation they had just loaded disappear. `merge` is a set union keyed on the row's
   * own primary key, so it cannot duplicate a message and cannot drop one that has scrolled
   * out of the newest page — the two ways a "clever" merge goes wrong.
   *
   * A failed poll leaves what is on screen alone. The thread going blank because one fetch
   * timed out would read as the conversation having been lost.
   */
  const refresh = useCallback(async () => {
    inFlightFor.current = objectiveId;
    const page = await getChat({ limit: PAGE, ...(objectiveId ? { objectiveId } : {}) });
    // The screen moved to another plan while this was in the air. Dropped rather than
    // merged: see `inFlightFor`.
    if (inFlightFor.current !== objectiveId) return;
    if (isError(page)) {
      // Only worth saying when there is nothing on screen. A poll that fails while a
      // thread is being read needs no announcement — the next one is twenty seconds away
      // and the conversation is still there — where a first read that fails leaves a blank
      // panel that has to be explained.
      if (!loaded.current) setLoadFailed(true);
      return;
    }
    loaded.current = true;
    // Which plan the page that just arrived actually is. The API resolves the default, so
    // this is the only way the screen knows — see the reset effect, which needs it to tell
    // "a notification for the plan already on screen" from "a different conversation".
    showing.current = page.objectiveId;
    setLoadFailed(false);
    setMessages((held) => merge(held, page.messages));
    /*
     * The cursor is only taken from the *first* read.
     *
     * A poll's cursor points at the bottom of the newest page, which is not where the
     * learner has read back to — taking it would send the history walk to fetch a page
     * they already have and leave it pointing at the same place for ever.
     */
    setOlderCursor((held) => held ?? page.olderCursor);
    /*
     * The icon, from the server's count rather than from anything counted here (F-120).
     *
     * Set on every poll, absolutely, because an icon that is only ever adjusted drifts the
     * first time an event is missed and then carries a number nobody can account for. If the
     * learner is at the newest message, `reportSeen` below clears it within the same tick —
     * so a badge appearing for a moment on a screen somebody is reading is the honest
     * intermediate state, not a flicker to design around.
     *
     * Absent on a history page, where a count would describe the wrong window entirely.
     *
     * **The number is the learner's and not this thread's** (F-151 step 8). `page.unread`
     * counts one plan — since step 8 that is a true count of one plan, against that plan's own
     * marker — and the Badging API puts one number on one icon, so the icon owes a total.
     * `iconCount` adds the plans up with this thread's fresher figure standing in for its own.
     * Setting the thread's count alone would clear the icon for a question waiting in another
     * plan, which is the same class of quiet wrongness the marker rewrite exists to remove.
     */
    if (page.unread !== undefined) {
      const shown = page.objectiveId ?? objectiveId;
      const total = iconCount({
        objectives: plansRef.current,
        thread: { objectiveId: shown, unread: page.unread },
      });
      if (total !== undefined) void showBadge(total);
      /*
       * **This plan's own tab follows the poll, and not only the icon** — a review finding,
       * and the fix is here rather than in the tab bar because this is the only place that
       * learns the fresher number. The plans list is read when the screen opens; without this
       * the tab above the conversation would go on showing "3" while the learner read all
       * three, for as long as they stayed on the screen, with the icon beside it saying none.
       *
       * Replaced only when the number actually moved, because this runs every twenty seconds
       * and a new array every time is a re-render of the bar for nothing.
       */
      if (shown) {
        const fresh = page.unread;
        const held = plansRef.current;
        if (held?.some((plan) => plan.objectiveId === shown && plan.unread !== fresh)) {
          const updated = held.map((plan) =>
            plan.objectiveId === shown ? { ...plan, unread: fresh } : plan,
          );
          plansRef.current = updated;
          setPlans(updated);
        }
      }
    }
    /*
     * A pending bubble goes as soon as the real row arrives, and not when the request
     * returns. The row is the one the server wrote while claiming the message — before
     * anything was graded — so it cannot fail to appear once the send succeeded, and
     * matching on it rather than on the response means a send whose *response* was lost
     * still resolves on the next poll instead of leaving a bubble on screen for ever.
     *
     * Matched on the id's tail because the server files the message under its own prefix
     * and its own instant: the row's id is `<when>#web_<what we sent>`.
     */
    setPending((waiting) =>
      waiting.filter(
        (item) =>
          item.failed !== undefined ||
          !page.messages.some((message) => message.id.endsWith(`#web_${item.messageId}`)),
      ),
    );
    // `objectiveId` is a dependency because the thread it reads depends on it: a tap that
    // moves the screen to another plan must not go on polling the previous one.
  }, [objectiveId]);

  /**
   * The objective's name and the learner's progress, for the header.
   *
   * Two calls and not one, because they answer two different questions and one of them can
   * be null while the other is not: an objective in `review` has a name and no progress at
   * all, which is the ordinary state of a learner in their first hour. Failures are
   * swallowed into `null` rather than surfaced — the header degrades to a dash and the
   * conversation, which is what the screen is for, is unaffected by a progress query that
   * timed out.
   */
  const readContext = useCallback(async () => {
    const asked = objectiveId;
    const [objectiveResult, progressResult] = await Promise.all([
      currentObjective(),
      // The same plan the thread was read for, so the header cannot report one plan's
      // percentage above another plan's conversation.
      getProgress(objectiveId),
    ]);
    // The screen moved while these were in the air. See `inFlightFor` — a header naming the
    // previous plan above this one's thread is the same fault as an interleaved thread, and
    // quieter.
    if (asked !== objectiveId) return;
    if (!isError(objectiveResult)) {
      /*
       * **The plan on screen, not the one the API would default to**, which is the whole of
       * why `objectives` was added to that response.
       *
       * `Header` takes the objective's title for the name and its description for the "what
       * you asked for" panel, so an unqualified `currentObjective()` puts plan A's title and
       * A's own words above B's thread and B's percentage — the failure the line above claims
       * to have removed, arriving through the other half of the same header. Falls back to
       * the default when the named plan is not in the live list, which is a plan that has
       * been closed since the notification was sent: a stale name is better than a blank
       * header over a thread that is plainly there.
       */
      const named = objectiveResult.objectives?.find(
        (plan) => plan.objectiveId === objectiveId,
      );
      setObjective(named ?? objectiveResult.objective);
      setPlans(objectiveResult.objectives);
      plansRef.current = objectiveResult.objectives;
      /*
       * The icon, the moment the counts arrive (F-151 step 8).
       *
       * The thread's own count comes from the poll and is left out here: this answer is the
       * only place the *other* plans' counts exist, and waiting for the next poll to add them
       * up would leave the icon a plan short for up to twenty seconds after the app opens.
       * `undefined` — no plan carried a count — leaves the badge alone rather than clearing it.
       */
      const total = iconCount({ objectives: objectiveResult.objectives });
      if (total !== undefined) void showBadge(total);
    }
    if (!isError(progressResult)) setProgress(progressResult.progress);
  }, [objectiveId]);

  /*
   * A different plan is a different conversation, so everything held about the last one goes.
   *
   * **`merge` is a union and would otherwise interleave two threads.** It keys on the row's
   * own primary key, which is exactly right for two pages of one conversation and exactly
   * wrong for two conversations: without this, tapping a notification for plan B while
   * looking at plan A would show both plans' worth of messages in one list, sorted by
   * time, with nothing on screen to say why. The history cursor goes with them — it points
   * into the other plan's partition — and so does `loaded`, so a failed first read of the new
   * thread is reported rather than hidden by the old one having succeeded.
   *
   * **It is compared against the plan on screen, not against the previous `?o=`, and the
   * difference is a regression a review caught rather than a nicety.** A learner sitting on
   * `/chat` has no `?o=` at all — the API resolved their plan for them — so a notification
   * about *that same plan* moves `objectiveId` from `undefined` to an id, which on a
   * previous-value comparison reads as a different conversation and throws away the history
   * she had paged back through and the place she was reading at. She tapped it **while
   * scrolled up reading a briefing**, which is the exact complaint this whole notification
   * path was rebuilt over. `page.objectiveId` is what the API actually answered with, so
   * arriving at the plan already loaded is correctly a no-op.
   *
   * Nothing happens before the first page lands — `showing` is `undefined` and so is
   * everything this would clear — and nothing at all for a learner who never arrives with
   * `?o=`.
   */
  useEffect(() => {
    if (!objectiveId || objectiveId === showing.current) return;
    setMessages(undefined);
    setOlderCursor(undefined);
    setOlderFailed(false);
    setProgress(undefined);
    setObjective(undefined);
    loaded.current = false;
    showing.current = undefined;
  }, [objectiveId]);

  useEffect(() => {
    void refresh();
    void readContext();
  }, [refresh, readContext]);

  /*
   * The poll, stopped while the app is in the background.
   *
   * A phone with the app installed to the home screen sits in a pocket for hours, and a
   * timer that kept firing there would be a request every twenty seconds for a screen
   * nobody is reading — the one cost in this feature that could actually add up. **Stopped
   * rather than slowed**: a background interval on iOS is throttled to something between
   * once a minute and never, so a "slow poll" is a promise the platform does not keep, and
   * the learner is told about anything urgent by a push (F-102) rather than by a tab they
   * are not looking at. It refreshes once on becoming visible again, which is also the
   * moment they most want it: they have just come back to answer something.
   */
  /*
   * A push refreshes the thread at once, instead of waiting out the poll.
   *
   * **The poll and `visibilitychange` between them miss the case that matters most**: the
   * learner who is looking straight at the chat when a question arrives. Nothing goes away and
   * comes back, so no visibility change fires, and the thread sits unchanged for up to twenty
   * seconds after a notification has told her something is there. Reported from a handset as
   * the question never arriving at all — she navigated to another screen and back, which is
   * this poll being restarted by hand.
   *
   * Every message from the worker refreshes, `open` as well as `push`. A tap that focuses the
   * app lands here too, and re-reading is cheap next to showing somebody the thread they were
   * notified about without the message in it.
   *
   * It does not scroll. `refresh` merges into the existing thread and the pin rule decides
   * what moves — so a learner scrolled up reading a briefing stays exactly where she is and
   * the "new messages" pill appears below her, which is the third thing she asked for and the
   * one piece of this that was already built.
   */
  useEffect(() => onServiceWorkerMessage(() => void refresh()), [refresh]);

  /*
   * The icon, put right whenever the app comes to the front (F-120).
   *
   * **The badge can be wrong while the app is closed and only the app can correct it.** The
   * worker sets it from the count the server sends with each push, which is right at the moment
   * it arrives and goes stale the moment anything is read on another device — or, as happened
   * on 2026-09-16, when an older worker was setting it from something else entirely and left a
   * 26 on the icon that no push would ever revise downwards.
   *
   * `refresh` already sets the badge from the server's count, so bringing the app forward is
   * enough to reconcile. The thread's own poll stops while hidden and restarts on the same
   * event, which is why this rides alongside it rather than owning a timer: the badge should
   * never be the reason a request is made.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  useEffect(() => {
    let thread: ReturnType<typeof setInterval> | undefined;
    let context: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (thread === undefined) thread = setInterval(() => void refresh(), POLL_MS);
      if (context === undefined) context = setInterval(() => void readContext(), PROGRESS_MS);
    };
    const stop = () => {
      if (thread !== undefined) clearInterval(thread);
      if (context !== undefined) clearInterval(context);
      thread = undefined;
      context = undefined;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
        void readContext();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, readContext]);

  /*
   * Telling the server how far the learner has actually read (F-120).
   *
   * **Hung off the existing "are they at the newest message?" rule rather than off a fetch.**
   * Polling is not reading: the thread refreshes every twenty seconds while the app is on
   * screen, and somebody scrolled up in last week's history is fetching the newest page
   * constantly without having looked at any of it. Marking seen there would clear the badge
   * for a message nobody saw. The two callers below are the two places this screen already
   * decides the learner is at the bottom — the same condition that clears the "new messages"
   * pill — so the badge and the pill cannot disagree about what counts as read.
   *
   * `reported` stops the same id being sent on every scroll event; the server refuses a
   * backwards move anyway, so this is about the request count rather than about correctness.
   * The badge is cleared here rather than waiting for the next poll to return zero, because
   * the next poll is twenty seconds away and the learner is looking at the message now.
   */
  const reported = useRef('');
  const reportSeen = useCallback((id: string) => {
    if (id === '' || id <= reported.current) return;
    reported.current = id;
    /*
     * This plan's count goes to nought and the others keep theirs (F-151 step 8). Clearing the
     * icon outright — what this line did while there was one plan per learner — would take a
     * question waiting in another plan off the icon because the learner read *this* one, and
     * nothing would put it back until the next push.
     *
     * `?? 0` for the case where nothing is known about any plan, which is the single-plan
     * behaviour this replaces: there is one thread, the learner is at the bottom of it, and
     * nought is the truth.
     */
    void showBadge(
      iconCount({
        objectives: plansRef.current,
        thread: { objectiveId: showing.current, unread: 0 },
      }) ?? 0,
    );
    /*
     * And this plan's own tab, for the same reason and at the same moment. The poll would do
     * it within twenty seconds, but the learner is looking at the bottom of the thread *now* —
     * a pill saying "2" above messages they have just read is the icon's old bug moved one
     * element down the page.
     */
    const shown = showing.current;
    const held = plansRef.current;
    if (shown && held?.some((plan) => plan.objectiveId === shown && plan.unread !== 0)) {
      const updated = held.map((plan) =>
        plan.objectiveId === shown ? { ...plan, unread: 0 } : plan,
      );
      plansRef.current = updated;
      setPlans(updated);
    }
    // Not awaited and never surfaced: a failed report costs one stale badge until the next
    // poll, which is not worth an error on a screen somebody is reading.
    void markChatSeen(id);
  }, []);

  const jumpToBottom = useCallback((smooth: boolean) => {
    const element = thread.current;
    if (!element) return;
    pinned.current = true;
    const top = bottomScrollTop(boxOf(element));
    settled.current = top;
    /*
     * Smooth only when the learner asked for it by tapping. A new message arriving at the
     * bottom of a thread they are already at should appear where it is — animating that is
     * a hundred milliseconds of the previous message sliding away while they read it, which
     * is a smaller version of the same complaint this whole screen was rebuilt over.
     */
    if (smooth) element.scrollTo({ top, behavior: 'smooth' });
    else element.scrollTop = top;
  }, []);

  /*
   * The one effect that is allowed to move the thread, and it asks permission first.
   *
   * A layout effect rather than an effect, because all three branches below have to land
   * before the browser paints — a restore applied after paint is a visible jump, which is
   * precisely the thing being prevented.
   *
   * The order matters. A restore wins over everything: the learner has just asked for
   * history and is by definition not at the bottom, so evaluating the pin first would
   * fight it. Otherwise, if they are at the bottom the thread follows; if they are not, it
   * does not move at all and the count of what they have missed goes up instead.
   */
  useLayoutEffect(() => {
    const element = thread.current;
    if (!element) return;

    const kept = keepFromEnd.current;
    if (kept !== null) {
      keepFromEnd.current = null;
      const top = restoredScrollTop(boxOf(element), kept);
      settled.current = top;
      element.scrollTop = top;
      /*
       * Written here rather than left to the scroll event this assignment will produce.
       *
       * The `ResizeObserver` below also fires on this change — the content just grew by a
       * page — and it runs at the end of layout, where a `scroll` event is dispatched
       * afterwards. So for one frame the observer can still be looking at the value from
       * before the prepend, and if that value was `true` it would re-pin the thread to the
       * bottom immediately after the restore put the learner where they asked to be.
       * Measuring it now closes that window.
       */
      pinned.current = isPinned({ ...boxOf(element), scrollTop: top });
      return;
    }

    const newest = messages?.[messages.length - 1];
    if (pinned.current) {
      if (newest) {
        lastSeen.current = newest.id;
        reportSeen(newest.id);
      }
      setUnread(0);
      jumpToBottom(false);
      return;
    }
    setUnread(arrivedSince(messages ?? [], lastSeen.current));
  }, [messages, pending, jumpToBottom]);

  /*
   * Content that changes height *after* it has rendered.
   *
   * The layout effect above runs when the message list changes, which is not the same
   * moment the thread's height settles: a long briefing reflows when the web font swaps in,
   * a bubble grows when its text wraps differently after the keyboard changes the width,
   * and the composer growing a row takes height away from the thread. Every one of those
   * leaves a learner who *was* at the bottom a screenful above it, staring at the message
   * before the one that just arrived.
   *
   * So the pin is re-applied whenever the content's own box changes, and — the important
   * half — only then. Somebody reading history is not moved by a reflow either.
   *
   * **Both boxes are watched, and the second one is the keyboard.** Losing the bottom of a
   * thread has two causes: the content growing taller, and the *scroller* growing shorter.
   * Only the first changes `content`'s height, so observing it alone misses the case that
   * matters most — the keyboard opening takes roughly 400px off the thread on a phone, the
   * content is unchanged because the messages are, and a learner who was reading the
   * question they are about to answer watches it slide under the keys. It then reads as
   * unread, because `distanceFromBottom` is suddenly a screenful.
   *
   * `pinned.current` is deliberately *not* recomputed here. It still holds the answer from
   * before the box changed, which is the honest one: the rule is that the thread follows
   * the newest message while the learner is at the newest message, and shrinking the window
   * around somebody is not them scrolling away.
   */
  useEffect(() => {
    const inner = content.current;
    const scroller = thread.current;
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (pinned.current) jumpToBottom(false);
    });
    if (inner) observer.observe(inner);
    if (scroller) observer.observe(scroller);
    return () => observer.disconnect();
  }, [jumpToBottom]);

  /**
   * A page of history, with the reader's place kept exactly.
   *
   * The distance from the bottom is measured *before* the state update, because after it
   * the content above the viewport has already grown and the number is gone. See
   * `restoredScrollTop`.
   */
  const loadOlder = useCallback(async () => {
    const element = thread.current;
    if (!olderCursor || loadingOlder || !element) return;
    setLoadingOlder(true);
    setOlderFailed(false);
    const box = boxOf(element);
    const asked = objectiveId;
    const page = await getChat({
      cursor: olderCursor,
      limit: PAGE,
      ...(objectiveId ? { objectiveId } : {}),
    });
    setLoadingOlder(false);
    // A page of the previous plan's history, arriving after the screen moved. See
    // `inFlightFor` — the same hazard, and a history page is the worse half of it because
    // it is older rows, which sort above everything and are the least likely to be noticed.
    if (asked !== objectiveId) return;
    if (isError(page)) {
      // Said out loud, unlike a failed poll: this one was asked for, and a request that
      // silently does nothing reads as the history having run out.
      setOlderFailed(true);
      return;
    }
    keepFromEnd.current = distanceFromEnd(box);
    setMessages((held) => merge(held, page.messages));
    setOlderCursor(page.olderCursor);
  }, [olderCursor, loadingOlder, objectiveId]);

  /**
   * Every scroll of the thread, which is the only place `pinned` is ever written.
   *
   * Cheap on purpose — two subtractions and a comparison, no layout reads beyond the three
   * properties, no state set unless something actually changed. This runs on every frame of
   * a flick through a fortnight of history.
   */
  const onScroll = () => {
    const element = thread.current;
    if (!element) return;
    const box = boxOf(element);

    const wasSettled = settled.current !== null && Math.abs(box.scrollTop - settled.current) < 1;
    settled.current = null;

    const nowPinned = isPinned(box);
    pinned.current = nowPinned;
    if (nowPinned) {
      const newest = messages?.[messages.length - 1];
      if (newest) {
        lastSeen.current = newest.id;
        reportSeen(newest.id);
      }
      if (unread !== 0) setUnread(0);
    }

    // Not on our own restore. See `settled` — an echo treated as a scroll asks for the next
    // page, which asks for the next, until the whole log is in memory.
    if (!wasSettled && wantsOlder(box)) void loadOlder();
  };

  /**
   * Puts one message on its way, whether it is new or being sent again.
   *
   * One function for both, because the *only* difference is where the bubble came from —
   * and a second copy would be a second place to get the retry's guarantee wrong. What
   * makes a retry safe is that the id and the instant are the ones the first attempt used:
   * the server files a message under both, so an attempt that actually arrived is refused
   * as already handled rather than graded again — a second model call, a second correction,
   * and a memory model advanced twice for one answer.
   */
  async function deliver(item: Pending) {
    setSending(true);
    // Any previous failure on this bubble goes now, so a retry does not sit there still
    // saying it failed while it is in flight.
    setPending((waiting) =>
      waiting.map((waited) =>
        waited.messageId === item.messageId
          ? { messageId: item.messageId, text: item.text, sentAt: item.sentAt }
          : waited,
      ),
    );

    const result = await sendChatMessage({
      text: item.text,
      messageId: item.messageId,
      sentAt: item.sentAt,
      // The plan the answer belongs to, when the screen was told which. See `objectiveId`.
      ...(objectiveId ? { objectiveId } : {}),
    });
    setSending(false);

    if (isError(result)) {
      setPending((waiting) =>
        waiting.map((waited) =>
          waited.messageId === item.messageId ? { ...waited, failed: failureOf(result) } : waited,
        ),
      );
      return;
    }

    // The reply is not in the response on purpose — one reader of the thread, so nothing on
    // screen can disagree with a reload. It is in the log by the time this resolves. The
    // header is re-read with it: an answer is the one thing that moves the percentage, and
    // this is the moment somebody looks at it.
    await refresh();
    void readContext();
  }

  async function send() {
    const text = draft.trim();
    if (text === '' || sending) return;

    const item: Pending = { messageId: newMessageId(), text, sentAt: new Date().toISOString() };
    /*
     * The one place the pin is set by anything but a measurement.
     *
     * Saying something is an unambiguous request to be where the conversation is: nobody
     * types an answer in order to keep reading last week. Without this, answering from half
     * way up the thread would put the reply somewhere below the fold and show a "new
     * messages" badge for a message the learner wrote themselves.
     */
    pinned.current = true;
    // The box is cleared now rather than when the request comes back, because the message is
    // already on screen as a pending bubble — text left in the box beside it reads as the
    // message not having been sent.
    setDraft('');
    setPending((waiting) => [...waiting, item]);
    await deliver(item);
  }

  /**
   * The sources behind the tapped message, fetched when the sheet opens and never before.
   *
   * **Not part of the thread read, and that is the whole reason it is a second request.**
   * `GET /api/chat` is re-read every twenty seconds by every open chat; resolving citations
   * costs the server a source-row query and up to four S3 reads per message that has any, so
   * carrying them on the page read would bill every learner thousands of reads a day for a
   * panel almost nobody opens. See `citations.ts`.
   *
   * `live` guards the write, for the ordinary case rather than an exotic one: tapping one
   * chip, closing the sheet and tapping another leaves the first request in flight, and
   * without this its answer lands on the second message's panel — the learner is shown the
   * provenance of a briefing they are no longer looking at, with nothing saying so.
   */
  useEffect(() => {
    const nodeId = asked?.nodeId;
    const itemId = asked?.itemId;
    if (!nodeId || !itemId) {
      setProvenance(undefined);
      return;
    }

    let live = true;
    setProvenance({ state: 'loading' });
    void (async () => {
      const result = await getMessageSources(nodeId, itemId);
      if (!live) return;
      setProvenance(
        isError(result)
          ? { state: 'failed' }
          : { state: 'read', sources: result.sources, cited: result.cited },
      );
    })();
    return () => {
      live = false;
    };
  }, [asked, attempt]);

  /*
   * ---- The first run's worked examples (F-141, step 3) ----
   *
   * Everything in this block is inert for every learner but one: somebody who walked through the
   * reach-window gate a few minutes ago and has not yet been shown what a briefing and a question
   * look like. `useFirstRun` answers `open` for everybody else — including every learner who
   * existed before this shipped — and each of the three pieces below is gated on `tour`.
   */
  const { stage: firstRunStage, mark: markFirstRun } = useFirstRun();
  const showSamples = firstRunStage === 'tour';

  /**
   * The learner's own subject titles, for the slots in the samples.
   *
   * **From the research progress and not from `progress`**, which is the read the rest of this
   * screen uses and which is `null` for the whole of this state: *"a plan awaiting approval is not
   * a plan at nought per cent"*, and during the tour there is no plan at all. The subject titles do
   * exist by then — the outline writes all twelve of them about forty seconds in — and they live on
   * the research rows.
   *
   * Asked once, not polled. A sample whose subject changed under the learner while they were
   * reading it would be a distraction from the shape, which is the only thing this is teaching; and
   * an empty answer is handled rather than avoided — `sampleSlots` falls back to the objective's
   * own title and then to fixed copy, so a tour reached in the first forty seconds still works.
   */
  const [sampleSubjects, setSampleSubjects] = useState<readonly string[]>([]);
  useEffect(() => {
    if (!showSamples || !objective) return;
    let alive = true;
    void getResearchProgress(objective.objectiveId).then((result) => {
      if (!alive || isError(result) || !result.run) return;
      setSampleSubjects(result.subjects.map((subject) => subject.title));
    });
    return () => {
      alive = false;
    };
  }, [showSamples, objective?.objectiveId]);

  /*
   * The tour ends by itself the moment there is something real to look at.
   *
   * The "Got it" button is the ordinary way out and this is the one that matters: a learner who
   * leaves the examples on screen and comes back an hour later to their first actual briefing must
   * not find two illustrations sitting above it. An example is only worth anything before the
   * thing it illustrates exists.
   */
  useEffect(() => {
    if (showSamples && (messages?.length ?? 0) > 0) markFirstRun('done');
  }, [showSamples, messages?.length, markFirstRun]);

  /** Every subject of the running objective, by the id a message carries. */
  const subjects = useMemo(() => {
    const byNode = new Map<string, SubjectProgress>();
    for (const subject of progress?.subjects ?? []) byNode.set(subject.nodeId, subject);
    return byNode;
  }, [progress]);

  const conversation = messages ?? [];

  /*
   * What to offer above the box, from the state the learner is actually in.
   *
   * The last thing *we* said decides most of it, so it is read off the thread rather than
   * asked for: the newest outbound message is the one `MORE` would elaborate and the one
   * `DISPUTE` would contest. See `chat/suggest.ts`.
   */
  const suggestions = useMemo(() => {
    let lastOutbound: ChatMessage | undefined;
    for (let index = conversation.length - 1; index >= 0; index -= 1) {
      const message = conversation[index];
      if (message && message.direction === 'out') {
        lastOutbound = message;
        break;
      }
    }
    const load = progress?.load;
    return suggestionsFor({
      lastOutbound,
      awaitingAnswer: load?.awaitingAnswer ?? false,
      dayIsSpent:
        load != null &&
        load.dailyLimit !== null &&
        load.sentToday !== null &&
        load.sentToday >= load.dailyLimit,
    });
  }, [conversation, progress]);

  const askedSubject = asked?.nodeId !== undefined ? subjects.get(asked.nodeId) : undefined;

  /**
   * The thread, built once per change to the thread.
   *
   * **Not cheap enough to do on every render, and this component renders on every
   * keystroke.** `dayOf` calls `toLocaleDateString`, which is an `Intl` formatting call; a
   * learner three weeks in has a couple of hundred messages on screen, so without this the
   * composer costs two hundred `Intl` calls per character typed — on the screen where
   * typing is the whole point, on a phone. Memoised, typing touches none of it.
   *
   * It holds because everything it depends on is stable across a render that only changed
   * the draft: `merge` returns the same array when the poll changed nothing, `subjects` is
   * memoised on the progress read, `setAsked` is a state setter, and `t` is memoised per
   * locale by the provider in `App.tsx` — if that last one ever stops being true this memo
   * quietly stops holding, which is why it is named here.
   */
  const rows = useMemo(() => {
    let lastDay = '';
    return conversation.map((message) => {
      const day = dayOf(message.at, locale, t);
      const newDay = day !== '' && day !== lastDay;
      if (newDay) lastDay = day;
      const subject = message.nodeId ? subjects.get(message.nodeId) : undefined;
      return (
        <div key={message.id} className="chat-slot">
          {newDay && <p className="chat-day">{day}</p>}
          <Bubble
            message={message}
            subject={subject ? localised(subject.title, locale, t('chat.subject.untitled')) : undefined}
            onSubject={setAsked}
          />
        </div>
      );
    });
  }, [conversation, subjects, locale, t]);

  return (
    /*
     * `fills` is the shell's own hook: `styles.css` gives `.stage > .fills` a
     * `flex: 1 1 auto; min-height: 0`, which is what makes this box exactly the height left
     * over after the fixed top bar. It is the whole of the height contract and there is no
     * JavaScript in it — see `chat/viewport.ts`, which measures the keyboard and nothing
     * else.
     */
    <>
      {/*
       * The plans, above the conversation (F-151 step 8).
       *
       * A sibling of the chat rather than a child of it, and both halves of that matter. It is
       * inside the fragment so that `.stage`'s flex column still sees `.fills` as a direct
       * child — the whole height contract is `.stage > .fills`, and a wrapper around the two
       * would silently leave the thread sized by its content instead of by the viewport. And it
       * is outside `.chat-screen` so that the bar does not scroll away with the thread or sit
       * between the header and the messages.
       *
       * Nothing renders here for a learner with one plan, so today's chat is exactly the box it
       * was before this step.
       */}
      {/* `objective` and not `showing.current`: the header's plan is state and a ref read
          during a render is a value React has not promised to have re-rendered for. The two
          agree — both are "the plan named, or the one the API defaulted to". */}
      <ObjectiveTabs
        objectives={plans}
        selected={objectiveId ?? objective?.objectiveId}
        path={ROUTES.chat}
      />
      <section className="chat-screen fills" ref={root}>
      {/* `?diag=1` only, and linked from nowhere. See `Diagnostics`: three fixes for the iOS
          keyboard have shipped on reasoning alone and all three were wrong. */}
      {debugOn() && <Diagnostics root={root} />}
      <Header objective={objective} progress={progress} />

      <div
        className="chat-thread"
        ref={thread}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        aria-label={t('chat.title')}
      >
        <div className="chat-thread-inner" ref={content}>
          {/*
            The worked examples, above everything, once per learner (F-141, step 3).

            **Above the history rather than at the end of it**, which is the one placement that
            says what they are without a word: the thread runs oldest-first, so anything at the
            bottom is the most recent thing that happened to you. These did not happen. Putting
            them at the top makes them the preamble to a conversation that has not started, which
            is exactly what they are — and it is also where they stay put while the real first
            briefing arrives underneath them.

            They are an `<aside>` and not a `.chat-slot`, so they are not entries in this thread's
            message column — but they *are* inside its `role="log"`, which is why `Samples` sets
            `aria-live="off"` on itself rather than relying on being outside it. And nothing here
            writes to `MSG#`: they are rendered, not sent. See `onboarding/Samples.tsx`, which
            carries the whole of that constraint.

            **Held back until the thread has been read**, which costs nothing and removes a flash:
            a learner whose research produced a `learning_started` notice already has a message, so
            rendering before `messages` lands would put two examples on screen for one frame and
            then take them away again. `undefined` is "not asked yet" here, exactly as it is
            everywhere else on this screen.
          */}
          {showSamples && messages !== undefined && (
            <Samples
              subjects={sampleSubjects}
              objective={
                objective ? localised(objective.title, locale, t('tour.subject.fallback')) : undefined
              }
              onDone={() => markFirstRun('done')}
            />
          )}
          {/*
            The top of the history, which says which of three things it is.
            Loading, finished, or a button — because the automatic fetch on scroll is a
            convenience and not the mechanism: a keyboard user who never generates a scroll
            event, and a learner whose fetch failed, both still need a way back.
          */}
          {messages !== undefined && (
            <p className="chat-older">
              {loadingOlder ? (
                <span className="chat-muted">{t('chat.older.loading')}</span>
              ) : olderCursor ? (
                <button
                  type="button"
                  className={olderFailed ? 'chat-linky chat-problem' : 'chat-linky'}
                  onClick={() => void loadOlder()}
                >
                  {olderFailed ? t('chat.older.again') : t('chat.older')}
                </button>
              ) : (
                conversation.length > 0 && (
                  <span className="chat-muted">{t('chat.older.end')}</span>
                )
              )}
            </p>
          )}

          {messages === undefined && !loadFailed && (
            <p className="chat-muted chat-centred">{t('chat.loading')}</p>
          )}
          {loadFailed && messages === undefined && (
            <p className="chat-problem chat-centred">{t('chat.error.load')}</p>
          )}
          {/* Silent while the examples are up: "nothing has been asked of you yet" is true, and
              printing it under two illustrations of things that will be asked reads as the screen
              contradicting itself. The examples already say the thread is empty, and say it in the
              useful direction. */}
          {messages !== undefined &&
            conversation.length === 0 &&
            pending.length === 0 &&
            !showSamples && <p className="chat-muted chat-centred">{t('chat.empty')}</p>}

          {rows}

          {/* The learner's own message, before the server has confirmed it. A chat that
              waits for a round trip before showing what somebody typed feels broken on a
              slow connection, which is exactly where it matters most. */}
          {pending.map((item) => (
            <article className="chat-msg chat-msg-said is-pending" key={item.messageId}>
              <p className="chat-msg-kind">{t('chat.kind.said')}</p>
              <div className="chat-msg-body">
                {/* Through the same renderer as a confirmed message, so a reply the learner
                    typed with dashes in it does not visibly change shape the moment the
                    server acknowledges it — the flicker would read as the message having
                    been altered on the way. */}
                <Prose text={item.text} />
              </div>
              {item.failed ? (
                <p className="chat-problem chat-small">
                  {t(item.failed)}{' '}
                  {item.failed !== 'chat.error.graded' && item.failed !== 'chat.error.session' && (
                    <button
                      type="button"
                      className="chat-linky"
                      onClick={() => void deliver(item)}
                      disabled={sending}
                    >
                      {t('chat.retry')}
                    </button>
                  )}
                </p>
              ) : (
                <p className="chat-msg-foot">
                  <span className="chat-msg-time">{t('chat.sending')}</span>
                </p>
              )}
            </article>
          ))}
        </div>
      </div>

      {/*
        What arrived while they were reading, and nothing more than that.

        **This is the other half of the fix.** Not scrolling somebody is only half an
        answer: without a way back they either miss what arrived or have to flick to the
        bottom to find out whether anything did. The pill says how many and takes them
        there when *they* choose, which is the whole of the difference from the behaviour
        that was complained about.

        It is outside the scroller rather than inside it, so it does not move with the
        content, and it hangs off a rail of no height between the thread and the composer —
        so it is directly above the box whatever height the box has taken, and its
        appearing costs the thread no height at all.
      */}
      <div className="chat-unread-rail">
        {unread > 0 && (
          <button type="button" className="chat-unread" onClick={() => jumpToBottom(true)}>
            {(unread === 1 ? t('chat.new.one') : t('chat.new.many')).replace('{n}', String(unread))}
            <span aria-hidden="true"> ↓</span>
          </button>
        )}
      </div>

      <Composer
        draft={draft}
        onDraft={setDraft}
        onSend={() => void send()}
        sending={sending}
        suggestions={suggestions}
      />

      {/*
        Where a message came from, and how the learner is doing there.

        The subject's own counts rather than the objective's: "you are 41% across
        everything" is not what somebody tapping a question about the cardiac cycle is
        asking. The same three-way split as the header's sheet, for the reason
        `Progress.tsx` argues — "not asked yet" and "got wrong" are the only two things
        here anybody can act on differently.
      */}
      {asked && (
        <Sheet title={t('chat.subject.title')} onClose={() => setAsked(undefined)}>
          {askedSubject ? (
            <>
              <p className="chat-sheet-lede">
                {localised(askedSubject.title, locale, t('chat.subject.untitled'))}
              </p>
              <p className="chat-sheet-note">
                {askedSubject.mastery === null
                  ? t('chat.subject.unmeasured')
                  : `${percent(askedSubject.mastery)} · ${t('chat.prog.mastered')}`}
              </p>
              <div className="chat-figures">
                <div className="chat-figure">
                  <span className="chat-figure-n">{askedSubject.evidence.holding}</span>
                  <span className="chat-figure-label">{t('chat.prog.holding')}</span>
                </div>
                <div className="chat-figure">
                  <span className="chat-figure-n">{askedSubject.evidence.struggling}</span>
                  <span className="chat-figure-label">{t('chat.prog.struggling')}</span>
                </div>
                <div className="chat-figure">
                  <span className="chat-figure-n">{askedSubject.evidence.untested}</span>
                  <span className="chat-figure-label">{t('chat.prog.untested')}</span>
                </div>
              </div>
              <p className="chat-sheet-note">
                {t('chat.prog.of').replace('{n}', String(askedSubject.evidence.questions))}
              </p>
              {/* Unticked on the plan, which does not mean its questions have stopped
                  arriving — the review states already written keep being scheduled. The
                  panel says so rather than letting the chip and the plan disagree. */}
              {!askedSubject.active && (
                <p className="chat-sheet-note">{t('chat.subject.inactive')}</p>
              )}
            </>
          ) : (
            <p className="chat-sheet-note">{t('chat.subject.gone')}</p>
          )}

          {/*
            Where the material came from, which is the other half of "where this came from".

            Leo, 2026-09-16: *"i want to be able to click to tap on a briefing and see where
            the data came from (sources)."* It is in this sheet rather than behind a second
            tap target because the sheet is already titled with his question and already the
            thing a tapped message opens — a separate affordance would be a second way to ask
            one question, and the F-112 sheet exists precisely so a learner can look at
            something *while* the briefing is still on screen.
          */}
          {provenance && <SourcePanel state={provenance} onRetry={() => setAttempt((n) => n + 1)} />}

          <p className="chat-sheet-note">
            {`${t(KIND_COPY[bubbleKind(asked)])} · ${clock(asked.at, locale)}`}
          </p>
        </Sheet>
      )}
      </section>
    </>
  );
}

/**
 * What the sheet knows about where the tapped message's material came from.
 *
 * `read` with an empty `sources` is a real answer and not a failure: a correction carries no
 * item, and every briefing written before the content store existed cites nothing. The panel
 * says so in words rather than rendering an empty list, because a heading with nothing under
 * it reads as a panel that broke.
 */
type Provenance =
  | { state: 'loading' }
  | { state: 'failed' }
  | { state: 'read'; sources: CitedSource[]; cited: number };

const SOURCE_KIND: Record<CitedSource['kind'], CopyKey> = {
  upload: 'chat.src.kind.upload',
  link: 'chat.src.kind.link',
  research: 'chat.src.kind.research',
};

/**
 * The sources behind a message, in terms a learner recognises.
 *
 * **The title leads and the id is never the headline.** A learner who does not yet know the
 * subject cannot judge a briefing on its own merits (F-95, and the reason the grounding
 * checks exist at all); what makes it judgeable is recognising that it came from the book
 * they photographed. `src_a8f2` is not that, so the id appears only where nothing else
 * survived — a source row deleted after the item was generated, which is reachable through
 * the delete offered on a failed upload.
 *
 * A URL is rendered as a link only when the API actually returned one. A research document is
 * assembled from several pages and has no single address; `citations.ts` refuses to invent
 * one, and nothing here may substitute the id for it. `noreferrer` as well as `noopener`
 * because the page being opened is a third party's and our path is not its business.
 */
function SourcePanel({ state, onRetry }: { state: Provenance; onRetry: () => void }) {
  const { t } = useI18n();

  return (
    <div className="chat-src">
      <h3 className="chat-src-title">{t('chat.src.title')}</h3>
      {state.state === 'loading' && <p className="chat-sheet-note">{t('chat.src.loading')}</p>}
      {state.state === 'failed' && (
        <p className="chat-sheet-note">
          <button type="button" className="chat-linky chat-problem" onClick={onRetry}>
            {t('chat.src.failed')}
          </button>
        </p>
      )}
      {state.state === 'read' && state.sources.length === 0 && (
        <p className="chat-sheet-note">{t('chat.src.none')}</p>
      )}
      {state.state === 'read' && state.sources.length > 0 && (
        <>
          <ul className="chat-src-list">
            {state.sources.map((source) => (
              <li className="chat-src-item" key={source.sourceId}>
                <p className="chat-src-name">{source.title ?? t('chat.src.untitled')}</p>
                <p className="chat-sheet-note">{t(SOURCE_KIND[source.kind])}</p>
                {source.url !== undefined && (
                  <p className="chat-sheet-note">
                    <a
                      className="chat-src-link"
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {source.url}
                    </a>
                  </p>
                )}
                {source.detail !== undefined && (
                  <p className="chat-sheet-note">{source.detail}</p>
                )}
                {/* The one thing on this panel anybody can act on: a briefing that reads thin
                    beside a source that could not be read has an explanation and a fix, and
                    without this the two look identical. */}
                {source.status === 'failed' && (
                  <p className="chat-sheet-note chat-problem">{t('chat.src.unreadable')}</p>
                )}
              </li>
            ))}
          </ul>
          {/* Only when the API trimmed the list. Showing three of forty without saying so
              would understate what the material rests on, which is the one direction this
              panel must not be wrong in. */}
          {state.cited > state.sources.length && (
            <p className="chat-sheet-note">
              {t('chat.src.trimmed').replace('{n}', String(state.cited))}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** The word for each kind of message, for the line at the foot of the subject sheet. */
const KIND_COPY: Record<BubbleKind, CopyKey> = {
  said: 'chat.kind.said',
  briefing: 'chat.kind.briefing',
  question: 'chat.kind.question',
  reminder: 'chat.kind.reminder',
  mark: 'chat.kind.mark',
  reply: 'chat.kind.reply',
  summary: 'chat.kind.summary',
  notice: 'chat.kind.notice',
};

/** Which copy explains a refused send. */
function failureOf(error: ApiError): CopyKey {
  return SEND_ERRORS[error.error] ?? 'chat.error.send';
}
